import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { isoDateInZone, wallTimeInZone } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import { readBoardRewards, readFamilyBoard } from '../../db/familyDisplay.js';
import type { BoardEvent, BoardPerson, BoardStars, FamilyBoard } from '../../db/familyDisplay.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { completeTask, reopenTask } from '../../db/tasks.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { useWakeLock } from '../../hooks/useWakeLock.js';
import { formatDay } from '../../format.js';

/**
 * Familienanzeige (27.10.2026): das Küchentablet.
 *
 * Kein eigener Kontotyp — ein Anzeigemodus in der App eines Erwachsenen,
 * Vollbild ohne Reiterleiste, große Schrift. Gezeigt wird nur, was jeder in
 * der Küche sehen darf (Auswahl in `familyBoard`). Der Abgleich läuft in der
 * Hülle weiter; die Anzeige liest live aus der lokalen Datenbank.
 */
export function FamilyDisplay({
  planning,
  onExit,
}: {
  /** Modul „Planen“ (E15): ohne es keine Termine und Aufgaben. */
  planning: boolean;
  onExit: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const now = useNow(15_000);
  const awake = useWakeLock();
  const today = timeZone ? isoDateInZone(now, timeZone) : null;
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], undefined);
  const board = useLiveQuery(
    () =>
      householdId && today
        ? readFamilyBoard(householdId, today, db)
        : Promise.resolve(null as FamilyBoard | null),
    [householdId, today],
    undefined,
  );

  // Was ein Abhaken hier bucht, legen Erwachsene unter Haushalt fest (je Gerät).
  const rewards = useLiveQuery(() => readBoardRewards(db), [], true);

  // Escape beendet wie der Knopf — Tastaturbedienung.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onExit();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onExit]);

  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-7xl flex-col gap-4 px-4 py-4 md:px-6 md:py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 ref={heading} tabIndex={-1} className="sr-only">
            {t('family.heading')}
          </h1>
          <p className="kw-figure text-6xl md:text-8xl" aria-live="off">
            {timeZone ? wallTimeInZone(now, timeZone) : '–'}
          </p>
          <p className="mt-1 text-xl md:text-2xl">
            {today ? formatDay(today, 'full', i18n.language) : ''}
          </p>
        </div>
        <button type="button" onClick={onExit} className="kw-btn kw-btn-quiet px-6 text-lg">
          {t('family.exit')}
        </button>
      </header>

      {awake === 'unsupported' ? (
        <p role="status" className="text-sm text-ink-muted">
          {t('family.noWakeLock')}
        </p>
      ) : null}

      {householdId === null ? (
        <p role="status" className="kw-tile text-lg text-warn">
          {t('family.noHousehold')}
        </p>
      ) : null}

      {board ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {planning ? (
            <>
              <EventsTile
                id="family-today"
                title={t('family.today')}
                empty={t('family.noEventsToday')}
                events={board.today}
              />
              <EventsTile
                id="family-tomorrow"
                title={t('family.tomorrow')}
                empty={t('family.noEventsTomorrow')}
                events={board.tomorrow}
              />
              <TasksTile people={board.people} today={today!} rewards={rewards} />
            </>
          ) : null}
          <ShoppingTile shopping={board.shopping} />
          {board.stars.length > 0 ? <StarsTile stars={board.stars} /> : null}
        </div>
      ) : null}
    </main>
  );
}

/** Die Uhr: tickt in `ms`, damit Uhrzeit und „heute“ nach Mitternacht stimmen. */
function useNow(ms: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

function EventsTile({
  id,
  title,
  empty,
  events,
}: {
  id: string;
  title: string;
  empty: string;
  events: BoardEvent[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={id} className="kw-tile flex flex-col gap-3 p-5">
      <h2 id={id} className="kw-kicker text-sm">
        {title}
      </h2>
      {events.length === 0 ? (
        <p className="text-xl text-ink-muted">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {events.map((event) => (
            <li key={event.id} className="flex gap-4">
              <span className="kw-figure w-20 shrink-0 text-2xl">
                {event.time ?? t('family.allDay')}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-xl font-semibold ${event.cancelled ? 'text-ink-muted line-through' : ''}`}
                >
                  {event.title}
                </span>
                <span className="block text-base text-ink-muted">
                  {[
                    event.cancelled ? t('family.cancelled') : null,
                    event.location,
                    event.forNames.length > 0
                      ? t('family.forWhom', { names: event.forNames.join(', ') })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TasksTile({
  people,
  today,
  rewards,
}: {
  people: BoardPerson[];
  today: string;
  /** `false`: Abhaken hier bucht weder Sterne noch Geld (Einstellung unter Haushalt). */
  rewards: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const me = currentOwnerId();
  const [done, setDone] = useState<{ id: string; title: string; undo: boolean } | null>(null);

  return (
    <section
      aria-labelledby="family-tasks"
      className="kw-tile flex flex-col gap-3 p-5 md:row-span-2"
    >
      <h2 id="family-tasks" className="kw-kicker text-sm">
        {t('family.tasks')}
      </h2>
      {people.length === 0 ? <p className="text-xl text-ink-muted">{t('family.noTasks')}</p> : null}
      {!rewards && people.some((person) => person.child) ? (
        <p className="text-base text-ink-muted">{t('family.noRewardsHere')}</p>
      ) : null}
      {people.map((person) => (
        <div key={person.userId ?? 'all'} className="flex flex-col gap-2">
          <h3 className="text-xl font-semibold">{person.name ?? t('family.everyone')}</h3>
          <ul className="flex flex-col gap-2">
            {person.tasks.map((task) => (
              <li key={task.id} className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-lg">{task.title}</span>
                  <span
                    className={`block text-sm ${task.state === 'overdue' ? 'text-warn' : 'text-ink-muted'}`}
                  >
                    {task.state === 'overdue' ? t('family.overdue') : t('family.dueToday')}
                    {task.dueTime ? ` · ${task.dueTime}` : ''}
                    {task.stars && rewards
                      ? ` · ${t('family.taskStars', { count: task.stars })}`
                      : ''}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label={t('family.completeNamed', { title: task.title })}
                  onClick={() => {
                    // Erledigt hat, wer dran ist — so bekommt ein Kind seine Sterne,
                    // außer die Eltern haben „zählt nicht“ gewählt (`noReward`).
                    void completeTask(
                      task.id,
                      { me, today, doneBy: person.userId ?? me, noReward: !rewards },
                      db,
                    );
                    setDone({ id: task.id, title: task.title, undo: !task.repeats });
                  }}
                  className="kw-btn kw-btn-quiet size-14 shrink-0 p-0 text-2xl"
                >
                  <span aria-hidden="true">✓</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {done ? (
        <p role="status" className="flex flex-wrap items-center gap-3 text-base">
          <span className="text-good">{t('family.completed', { title: done.title })}</span>
          {done.undo ? (
            <button
              type="button"
              className="kw-btn kw-btn-quiet kw-btn-small px-4"
              onClick={() => {
                void reopenTask(done.id, db);
                setDone(null);
              }}
            >
              {t('family.undo')}
            </button>
          ) : null}
        </p>
      ) : null}
    </section>
  );
}

function ShoppingTile({ shopping }: { shopping: FamilyBoard['shopping'] }): React.JSX.Element {
  const { t } = useTranslation();
  const rest = shopping.open - shopping.names.length;
  return (
    <section aria-labelledby="family-shopping" className="kw-tile flex flex-col gap-3 p-5">
      <h2 id="family-shopping" className="kw-kicker text-sm">
        {t('family.shopping')}
      </h2>
      {shopping.open === 0 ? (
        <p className="text-xl text-ink-muted">{t('family.shoppingEmpty')}</p>
      ) : (
        <>
          <p className="kw-figure text-5xl">
            <span aria-hidden="true">{shopping.open}</span>
            <span className="sr-only">{t('family.shoppingOpen', { count: shopping.open })}</span>
          </p>
          <ul className="flex flex-col gap-1 text-lg">
            {shopping.names.map((name, index) => (
              <li key={`${name}-${index}`}>{name}</li>
            ))}
          </ul>
          {rest > 0 ? (
            <p className="text-base text-ink-muted">{t('family.shoppingMore', { count: rest })}</p>
          ) : null}
        </>
      )}
    </section>
  );
}

function StarsTile({ stars }: { stars: BoardStars[] }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="family-stars" className="kw-tile flex flex-col gap-4 p-5">
      <h2 id="family-stars" className="kw-kicker text-sm">
        {t('family.stars')}
      </h2>
      {stars.map((child) => {
        const reached = child.goal !== null && child.balance >= child.goal.stars;
        const percent = child.goal
          ? Math.max(0, Math.min(100, (child.balance / child.goal.stars) * 100))
          : 0;
        return (
          <div key={child.userId} className="flex flex-col gap-2">
            <p className="flex items-baseline justify-between gap-3">
              <span className="text-xl font-semibold">{child.name}</span>
              <span className="kw-figure text-3xl">
                <span aria-hidden="true">★ {child.balance}</span>
                <span className="sr-only">{t('stars.count', { count: child.balance })}</span>
              </span>
            </p>
            {child.goal ? (
              <>
                <p className={`text-base ${reached ? 'text-good' : 'text-ink-muted'}`}>
                  {reached
                    ? t('stars.reached', { name: child.goal.name })
                    : t('stars.missing', {
                        count: child.goal.stars - child.balance,
                        name: child.goal.name,
                      })}
                </p>
                <span
                  className="kw-bar"
                  role="progressbar"
                  aria-label={t('family.starGoal', { name: child.name, goal: child.goal.name })}
                  aria-valuemin={0}
                  aria-valuemax={child.goal.stars}
                  aria-valuenow={Math.max(0, Math.min(child.goal.stars, child.balance))}
                >
                  <i
                    style={{
                      width: `${percent}%`,
                      backgroundColor: reached ? 'var(--color-good)' : undefined,
                    }}
                  />
                </span>
              </>
            ) : (
              <p className="text-base text-ink-muted">{t('family.noStarGoal')}</p>
            )}
          </div>
        );
      })}
    </section>
  );
}
